import { AngebotSparte } from '@mng/sales-types';
import {
  BicField,
  BooleanField,
  DateField,
  DependencyFunction,
  findObjectForPropertyInModelContext,
  findValueForPropertyInModelContext,
  IbanField,
  IModelContext,
  ModelDependency,
  ModelGroup,
  ModelItem,
  ModelItemOptions,
  ModelOption,
  NumberField,
  ReadOnlyFunction,
  requiredValueOptionsValidation,
  StringField,
  ValueOptions
} from '@mng/model';
import {
  createFzKontoBezeichnungField,
  createFzKontoDienststelleRequiredField,
  createFzKontoExklusivAbberNrField,
  createFzKontoKontoField,
  createFzKontoPersonalExplanationField,
  createFzKontoPersonalNummerRegexField,
  createFzKontoVerrechnungsartField,
  createFzPersNrErforderlichField
} from '../../../allgemein/fz-konto-base';
import { ITEM_ANGEBOTE_META_DATA, ITEM_ANGEBOTE_META_DATA_SPARTE } from '../../../antrag.model.paths';
import { AngebotMetaData, Antragdata, Person } from '../../../antrag.model.types';
import { antragContext } from '../../antrag-context';
import { IAntragFragen } from '../../logic/antrag-fragen';
import { LV_TARIF_TYP_FLV, LV_TARIF_TYP_ILV } from '../../logic/lv-tarif.helper';
import { createIsSparteGvDependencyFunction } from '../angebot.dependencies';
import { personalNummerValidation } from './zahlungsart.validation';

export const aktivOption: ModelOption = { name: 'Aktiv', value: 1 };
export const ruhestandOption: ModelOption = { name: 'Ruhestand', value: 2 };
export const karenzOption: ModelOption = { name: 'Karenz', value: 3 };
export const arbeitsStatusVNOptions: ValueOptions = new ValueOptions([aktivOption, ruhestandOption, karenzOption]);

export function zahlungsartAuswahlFilter(modelContext: IModelContext, baseOptions?: ModelOption[]): ModelOption[] {
  const antragdata: Antragdata = findObjectForPropertyInModelContext(modelContext, ITEM_ANGEBOTE_META_DATA);
  const angebot: AngebotMetaData = findObjectForPropertyInModelContext(modelContext, ITEM_ANGEBOTE_META_DATA_SPARTE);

  if (antragdata) {
    if (angebot && AngebotSparte.G === angebot.sparte) {
      const vn: Person = antragdata.personen.find((person) => person.personRollen.vn);
      if ((vn && vn.juristischePerson) || !angebot.zahlungsart?.fzKonto?.fzKontoUebernommen) {
        return ZAHLUNGSART_OPTIONS_OHNE_GK;
      }
    }
  } else {
    console.error('zahlungsartAuswahlFilter - antragdata not found');
  }

  return baseOptions;
}

export const zahlungsartModelDef = {
  createZahlungsartAuswahlModel(): ModelGroup {
    const zahlungsartAuswahl: ModelGroup = new ModelGroup('zahlungsart', 'Zahlungsart').addItems(
      // Zahlungsart
      new NumberField('zahlungsartAuswahl', '').withOptions(
        new ModelItemOptions([requiredValueOptionsValidation])
          .withValueOptions(zahlungsartValueOptions, zahlungsartAuswahlFilter)
          .withReadOnlyFunction(isZahlungsartReadOnly)
      ),
      // Zahlungsweise
      new NumberField('zahlungsweiseAuswahl', 'Zahlungsweise')
        .withOptions(
          new ModelItemOptions([requiredValueOptionsValidation])
            .withValueOptions(zahlungsweiseValueOptions)
            .withReadOnlyFunction(isZahlungsweiseReadOnly)
        )
        .withFunctionDependency(isZahlungsweiseRequired)
    );

    // GK
    zahlungsartAuswahl
      .addGroupObject(zahlungsartModelDef.createFzKonto().withFunctionDependency(isGKVerrechnungForGroupRequired))
      .addItems(
        new StringField('arbeitgeber', 'Arbeitgeber des Versicherungsnehmers')
          .withFunctionDependencies([
            isGKVerrechnungBlockForAngebotRequiredUndNichtUv,
            isVnArbeitgeberRequired,
            isNoGVAU
          ])
          .withMaxlength(256),
        new StringField('dienststelle', 'Dienststelle des Versicherungsnehmers')
          .withRemoveDataWhenNotRelevant(true)
          .withFunctionDependency(isDienststelleForGKVerrechnungRequired, false)
          .withMaxlength(80),
        new StringField('personalnummer', 'Personalnummer des Versicherungsnehmers')
          .withValidation(personalNummerValidation)
          .withRemoveDataWhenNotRelevant(true)
          .withFunctionDependencies([isGKVerrechnungForGroupRequired, isPersonalNummerErforderlich])
          .withMaxlength(10),
        new NumberField('arbeitsStatusVN', 'Arbeitsstatus des Versicherungsnehmers')
          .withRemoveDataWhenNotRelevant(true)
          .withValueOptions(arbeitsStatusVNOptions)
          .withFunctionDependencies(
            [createIsSparteGvDependencyFunction(), isGKVerrechnungForGroupRequired, isNoGVAU],
            false
          ),
        new DateField('eintrittsdatum', 'Eintrittsdatum des Versicherungsnehmer')
          .withRemoveDataWhenNotRelevant(true)
          .withFunctionDependencies([isGKVerrechnungBlockForAngebotRequiredUndNichtUv, isNoGVAU], false)
          .withRequiredFunction(eintrittsdatumRequired)
      );

    // SEPA
    zahlungsartAuswahl.addItems(...zahlungsartModelDef.createBankdatenFields());

    return zahlungsartAuswahl;
  },

  createZahlungParagraph3115Tarif(): ModelGroup {
    return new ModelGroup('zahlungLv3Tarif', 'Angaben zu LV §3 Tarifen').addItems(
      new NumberField('zahlungsartLv3', '').withOptions(
        new ModelItemOptions([requiredValueOptionsValidation]).withValueOptions(zahlungsart3115)
      )
    );
  },

  createBankdatenFields(): ModelItem[] {
    const bicField = new BicField('bic', 'BIC');
    return [
      new IbanField('iban', 'IBAN')
        .withFunctionDependency(bankdatenSichtbar)
        .withRequiredFunction(bankdatenRequired)
        .withRemoveDataWhenNotRelevant(true),
      bicField
        .withFunctionDependency(bicSichtbar, true, bicField)
        .withRequiredFunction(bicRequired)
        .withRemoveDataWhenNotRelevant(true),
      new StringField('kontoinhaber', 'Kontoinhaber')
        .withFunctionDependency(bankdatenSichtbar)
        .withRequiredFunction(bankdatenRequired)
        .withRemoveDataWhenNotRelevant(true)
        .withMaxlength(45)
    ];
  },

  createFzKonto(): ModelGroup {
    return new ModelGroup('fzKonto', 'FZ Konto').addItems(
      createFzKontoKontoField()
        .withFunctionDependency(isGKVerrechnungForGroupRequired)
        .withReadOnlyFunction(isFzKontoReadOnly),
      createFzKontoExklusivAbberNrField().withRemoveDataWhenNotRelevant().asArray(),
      // createFzKontoKontoField().withFunctionDependency(isGKVerrechnungForGroupRequired),
      createFzKontoVerrechnungsartField().withFunctionDependency(isGKVerrechnungForGroupRequired),
      createFzKontoBezeichnungField().withFunctionDependency(isGKVerrechnungForGroupRequired),
      createFzKontoPersonalNummerRegexField().withFunctionDependency(isGKVerrechnungForGroupRequired, false),
      createFzKontoPersonalExplanationField().withFunctionDependency(isGKVerrechnungForGroupRequired, false),
      createFzKontoDienststelleRequiredField().withFunctionDependency(isGKVerrechnungForGroupRequired, false),
      createFzPersNrErforderlichField().withFunctionDependency(isGKVerrechnungForGroupRequired, false),
      new BooleanField('fzKontoUebernommen', 'FZ Konto übernommen').withReadOnly(true)
    );
  }
};

export const ZAHLUNGSART_SEPA = 1;
export const ZAHLUNGSART_DAUERAUFTRAG = 2;
export const ZAHLUNGSART_ERLAGSCHEIN = 3;
export const ZAHLUNGSART_GK_VERRECHNUNG = 4;
export const ZAHLUNGSART_OPTIONS_OHNE_GK = [
  { name: 'SEPA-Lastschriftmandat', value: ZAHLUNGSART_SEPA },
  { name: 'Dauerauftrag', value: ZAHLUNGSART_DAUERAUFTRAG },
  { name: 'Erlagschein', value: ZAHLUNGSART_ERLAGSCHEIN }
];

export const zahlungsartOptions = [
  ...ZAHLUNGSART_OPTIONS_OHNE_GK,
  {
    name: 'GK-Verrechnung',
    value: ZAHLUNGSART_GK_VERRECHNUNG
  }
];

export const zahlungsartValueOptions: ValueOptions = new ValueOptions(zahlungsartOptions);

export const ZAHLUNGSWEISE_JAEHRLICH = 1;
export const ZAHLUNGSWEISE_MONATLICH = 12;
export const ZAHLUNGSWEISE_HALBJAEHRLICH = 2;
export const ZAHLUNGSWEISE_VIERTELJAEHRLICH = 3;

export const zahlungsweiseValueOptions: ValueOptions = new ValueOptions([
  { name: 'Monatlich', value: ZAHLUNGSWEISE_MONATLICH },
  { name: 'Vierteljährlich', value: ZAHLUNGSWEISE_VIERTELJAEHRLICH },
  { name: 'Halbjährlich', value: ZAHLUNGSWEISE_HALBJAEHRLICH },
  { name: 'Jährlich', value: ZAHLUNGSWEISE_JAEHRLICH }
]);

export const zahlungsart3115: ValueOptions = new ValueOptions([
  { name: 'Bezugsumwandlung', value: 1 },
  { name: 'Zuwendung durch den Dienstgeber', value: 2 }
]);

export const bankdatenSichtbar: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const angebotData: AngebotMetaData = findAngebotInContext(modelContext);
  return bankdatenForAngebotSichtbar(angebotData);
};

export const bicSichtbar: DependencyFunction = (dependency: ModelDependency, modelContext: IModelContext): boolean => {
  const angebotData: AngebotMetaData = findAngebotInContext(modelContext);
  return bicForAngebotSichtbar(angebotData);
};

export const isDienststelleForGKVerrechnungRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const angebotData: AngebotMetaData = findAngebotInContext(modelContext);
  return isGKVerrechnungBlockForAngebotRequired(angebotData) && isDienststelleForAngebotRequired(angebotData);
};

export const isGKVerrechnungBlockForAngebotRequiredUndNichtUv: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const angebotData: AngebotMetaData = findAngebotInContext(modelContext);
  return isGKVerrechnungBlockForAngebotRequired(angebotData) && angebotData.sparte !== AngebotSparte.U;
};

export function isDienststelleForAngebotRequired(angebotData: AngebotMetaData): boolean {
  return !!angebotData.zahlungsart.fzKonto && angebotData.zahlungsart.fzKonto.dienststelleRequired;
}

export const isGKVerrechnungForGroupRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const angebotData: AngebotMetaData = findAngebotInContext(modelContext);
  return isGKVerrechnungBlockForAngebotRequired(angebotData);
};

export const isZahlungsweiseRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const sparte: string = findValueForPropertyInModelContext(modelContext, ITEM_ANGEBOTE_META_DATA_SPARTE);
  return AngebotSparte.G === sparte;
};

export const isVnArbeitgeberRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  let isVnSelbstaendig = false;
  for (const person of modelContext.dataStack[0].personen) {
    if (person.personRollen.vn && person.stammdaten.beschaeftigungsart === 2) {
      isVnSelbstaendig = true;
    }
  }
  return !isVnSelbstaendig;
};

export const isZahlungsweiseReadOnly: ReadOnlyFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): boolean => {
  const angebotData: AngebotMetaData = findAngebotInContext(modelContext);
  return (
    angebotData.zahlungsart &&
    (ZAHLUNGSART_SEPA === angebotData.zahlungsart.zahlungsartAuswahl ||
      ZAHLUNGSART_DAUERAUFTRAG === angebotData.zahlungsart.zahlungsartAuswahl ||
      ZAHLUNGSART_GK_VERRECHNUNG === angebotData.zahlungsart.zahlungsartAuswahl)
  );
};

export const isFzKontoReadOnly: ReadOnlyFunction = (modelItem: ModelItem, modelContext: IModelContext): boolean => {
  const angebotData: AngebotMetaData = findAngebotInContext(modelContext);
  return (
    angebotData.zahlungsart &&
    angebotData.zahlungsart.fzKonto &&
    angebotData.zahlungsart.fzKonto.konto &&
    angebotData.zahlungsart.fzKonto.fzKontoUebernommen
  );
};

export function bankdatenForAngebotSichtbar(angebotData: AngebotMetaData): boolean {
  return !!(
    angebotData &&
    angebotData.zahlungsart &&
    (angebotData.zahlungsart.zahlungsartAuswahl === ZAHLUNGSART_SEPA ||
      angebotData.zahlungsart.zahlungsartAuswahl === ZAHLUNGSART_GK_VERRECHNUNG)
  );
}

export function bicForAngebotSichtbar(angebotData: AngebotMetaData): boolean {
  // This array is needed for the form validation if the NestJS service, since it doesn't override the  default required function
  // like the MngModelFieldBicComponent, which actually fetches those codes from a backend endpoint that retrieves them from a database.
  const staticEWRCountryCodes = [
    'BE',
    'BG',
    'DK',
    'DE',
    'EE',
    'FE',
    'FR',
    'GR',
    'IE',
    'IT',
    'HR',
    'LV',
    'LT',
    'LU',
    'MT',
    'NL',
    'AT',
    'PL',
    'PT',
    'RO',
    'SE',
    'SK',
    'SI',
    'ES',
    'CZ',
    'HU',
    'CY',
    'IS',
    'LI',
    'NO'
  ];

  return (
    bankdatenForAngebotSichtbar(angebotData) &&
    (angebotData.zahlungsart.bic?.length > 0 || angebotData.zahlungsart.iban?.length > 0) &&
    !staticEWRCountryCodes.includes(angebotData?.zahlungsart?.iban?.trim().slice(0, 2).toUpperCase())
  );
}

export function bicRequired(modelContext: IModelContext): boolean {
  const angebotData: AngebotMetaData = findAngebotInContext(modelContext);
  return bicForAngebotSichtbar(angebotData);
}

export function bankdatenRequired(modelContext: IModelContext): boolean {
  const angebotData: AngebotMetaData = findAngebotInContext(modelContext);
  return bankdatenRequiredForAngebot(angebotData);
}

export function bankdatenRequiredForAngebot(angebotData: AngebotMetaData): boolean {
  return !!(
    angebotData &&
    angebotData.zahlungsart &&
    (angebotData.zahlungsart.zahlungsartAuswahl === ZAHLUNGSART_SEPA ||
      (isGKVerrechnungBlockForAngebotRequired(angebotData) &&
        angebotData.zahlungsart.fzKonto &&
        (angebotData.zahlungsart.fzKonto.verrechnungsart === 'AB' ||
          (angebotData.zahlungsart.fzKonto.verrechnungsart === 'PL' && angebotData.sparte === AngebotSparte.G))))
  );
}

export function isGKVerrechnungBlockForAngebotRequired(angebotData: AngebotMetaData): boolean {
  return !!(
    angebotData &&
    angebotData.zahlungsart &&
    angebotData.zahlungsart.zahlungsartAuswahl === ZAHLUNGSART_GK_VERRECHNUNG
  );
}

export function eintrittsdatumRequired(modelContext: IModelContext): boolean {
  const angebotData: AngebotMetaData = findAngebotInContext(modelContext);
  return angebotData.sparte === AngebotSparte.G;
}

export const isPersonalNummerErforderlich: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const angebotData: AngebotMetaData = findAngebotInContext(modelContext);
  return angebotData?.zahlungsart?.fzKonto?.persNrErforderlich && angebotData?.gvGkKontoGvau !== true;
};

export const isNoGVAU: DependencyFunction = (dependency: ModelDependency, modelContext: IModelContext): boolean => {
  const angebotData: AngebotMetaData = findAngebotInContext(modelContext);
  return angebotData?.gvGkKontoGvau !== true;
};

export const isZahlungParagraph3115TarifRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const angebotData: AngebotMetaData = findAngebotInContext(modelContext);
  const fragenDefs: IAntragFragen =
    angebotData.tarifInformationMetaData && angebotData.tarifInformationMetaData.tarifschluessel
      ? antragContext.getFragenForTarif(angebotData.tarifInformationMetaData.tarifschluessel)
      : null;
  return !!(
    angebotData &&
    angebotData.tarifInformationMetaData &&
    angebotData.tarifInformationMetaData.tarifschluessel &&
    fragenDefs &&
    fragenDefs.paragraph_3_1_15
  );
};

export const isZahlungsartReadOnly: ReadOnlyFunction = (modelItem: ModelItem, modelContext: IModelContext): boolean => {
  const angebotData: AngebotMetaData = findAngebotInContext(modelContext);
  return (
    angebotData.praemienZahlweise === '0' ||
    angebotData.angestelltenRabatt ||
    (angebotData.zahlungsart &&
      angebotData.zahlungsart.fzKonto &&
      angebotData.zahlungsart.fzKonto.konto &&
      angebotData.zahlungsart.fzKonto.fzKontoUebernommen) ||
    (angebotData.tarifInformationMetaData && angebotData.tarifInformationMetaData.tarifschluessel
      ? antragContext.getFragenForTarif(angebotData.tarifInformationMetaData.tarifschluessel).paragraph_3_1_15
      : false) ||
    (angebotData.tarifInformationMetaData &&
      angebotData.tarifInformationMetaData.tarifTyp &&
      (angebotData.tarifInformationMetaData.tarifTyp === LV_TARIF_TYP_FLV ||
        angebotData.tarifInformationMetaData.tarifTyp === LV_TARIF_TYP_ILV))
  );
};

function findAngebotInContext(modelContext: IModelContext): AngebotMetaData {
  return findObjectForPropertyInModelContext(modelContext, ITEM_ANGEBOTE_META_DATA_SPARTE);
}
